import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Droplets } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { label: "Process", hash: "#process" },
  { label: "Services", hash: "#services" },
  { label: "Results", hash: "#results" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goTo = (hash) => {
    setOpen(false);
    if (location.pathname === "/") {
      window.__lenis?.scrollTo(hash, { offset: -72 });
    } else {
      navigate("/", { state: { scrollTo: hash } });
    }
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-2xl">
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center border border-aqua/50 text-aqua">
            <Droplets size={18} />
          </span>
          <span className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white">
            Clean <span className="text-aqua">&</span> Revive
          </span>
        </Link>

        <div className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => goTo(l.hash)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-aqua"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/about"
            data-testid="nav-link-about"
            className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:text-aqua"
          >
            About
          </Link>
        </div>

        <div className="hidden items-center gap-4 md:flex">
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-link"
                className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-aqua"
              >
                My bookings
              </Link>
              <button
                data-testid="nav-logout-button"
                onClick={logout}
                className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:text-white"
              >
                Log out
              </button>
            </>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-signin-link"
              className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:text-aqua"
            >
              Sign in
            </Link>
          )}
          <Link
            to="/book"
            data-testid="nav-book-button"
            className="group relative overflow-hidden border border-aqua bg-aqua px-6 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-aqua"
          >
            Book a clean
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="text-white md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-white/10 bg-obsidian px-6 py-6 md:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-5">
            {LINKS.map((l) => (
              <button
                key={l.hash}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                onClick={() => goTo(l.hash)}
                className="text-left font-mono text-xs uppercase tracking-[0.2em] text-zinc-300"
              >
                {l.label}
              </button>
            ))}
            <Link to="/about" data-testid="nav-mobile-link-about" onClick={() => setOpen(false)} className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">
              About
            </Link>
            {user ? (
              <Link to="/dashboard" data-testid="nav-mobile-dashboard" onClick={() => setOpen(false)} className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">
                My bookings
              </Link>
            ) : (
              <Link to="/auth" data-testid="nav-mobile-signin" onClick={() => setOpen(false)} className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">
                Sign in
              </Link>
            )}
            <Link
              to="/book"
              data-testid="nav-mobile-book-button"
              onClick={() => setOpen(false)}
              className="mt-2 inline-block w-fit border border-aqua bg-aqua px-6 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian"
            >
              Book a clean
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
